import dayjs, { Dayjs } from 'dayjs';
import { ApolloError } from '@apollo/client';

import { describeRateLimitRejection, getRateLimitRejection } from '../../../common/rateLimit';
import { DEFAULT_PAGE_SIZE, PAGE_SIZES, PageSize } from '../contacts/contactList';
import {
  CallDirection,
  CallMethod,
  CallOutcome,
  formatDuration,
  MAX_DURATION_SEC,
  METHODS,
  NOTES_MAX,
  OUTCOMES,
  parseDuration,
} from '../calls/logCall';
import { checkRecordingUrl } from '../calls/recording';

/**
 * The call log (card 21.6) as plain data and pure functions, tested without
 * rendering: the screen's state and its URL, the `crmCalls` filter it makes,
 * the words for each call, and the correction a caller sends.
 *
 * Backend reference: ilbp-backend docs/CARD_21_6_CALL_LOG.md (this screen and
 * its API additions) and CARD_16_CALL_LOGGING.md §6 (`crmCalls`).
 */

/* ------------------------------------------------------------------ *
 * What the API answers
 * ------------------------------------------------------------------ */

export interface CallLogRow {
  id: number;
  contactId: number;
  adminId: number | null;
  authorContactId: number | null;
  source: string;
  direction: CallDirection | null;
  method: CallMethod | null;
  outcome: CallOutcome | 'UNKNOWN';
  notes: string | null;
  durationSec: number | null;
  supportLevel: number | null;
  occurredAt: string;
  followUpAt: string | null;
  followUpDoneAt: string | null;
  followUpOpen: boolean;
  followUpDue: boolean;
  setDoNotCall: boolean;
  /** A link to the call's recording (card 21.7). */
  recordingUrl: string | null;
  editable: boolean;
  createdAt: string;
  updatedAt: string;
  contact: {
    id: number;
    fullName: string | null;
    primaryEmail: string | null;
    primaryPhone: string | null;
    doNotCall: boolean;
    doNotContact: boolean;
    mergedIntoId: number | null;
  } | null;
  admin: { id: number; firstName: string | null; lastName: string | null } | null;
  author: { id: number; fullName: string | null } | null;
  followUpDoneBy: { id: number; firstName: string | null; lastName: string | null } | null;
}

export interface OutcomeCount {
  outcome: CallOutcome | 'UNKNOWN';
  count: number;
}

export interface CallerCount {
  adminId: number | null;
  authorContactId: number | null;
  isYou: boolean;
  count: number;
  admin: { id: number; firstName: string | null; lastName: string | null } | null;
  author: { id: number; fullName: string | null } | null;
}

/* ------------------------------------------------------------------ *
 * The screen's state, all of it in the URL
 * ------------------------------------------------------------------ */

export type WhenId = 'today' | '7d' | '30d' | '90d' | '12m' | 'between';
export type FollowUpId = 'any' | 'open' | 'due' | 'done' | 'none';
export type SourceId = 'panel' | 'history';
export type RecordingId = 'with' | 'without';

export interface CallLogState {
  /** Words in the notes, as typed. */
  notes: string;
  /** One person's calls. */
  person: number | null;
  /** '' anyone; 'me'; 'a<id>' an admin; 'c<id>' a history caller with no account. */
  caller: string;
  outcomes: Array<CallOutcome | 'UNKNOWN'>;
  method: CallMethod | null;
  direction: CallDirection | null;
  source: SourceId | null;
  when: WhenId | null;
  /** For `between`: whole days, YYYY-MM-DD, both included; either may be open. */
  from: string;
  to: string;
  followUp: FollowUpId | null;
  /** Calls with a recording, or without one (card 21.7). */
  recording: RecordingId | null;
  page: number;
  pageSize: PageSize;
}

export const EMPTY_LOG_STATE: CallLogState = {
  notes: '',
  person: null,
  caller: '',
  outcomes: [],
  method: null,
  direction: null,
  source: null,
  when: null,
  from: '',
  to: '',
  followUp: null,
  recording: null,
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
};

/** The API's limits on the notes search (crm-calls.ts CALL_LIMITS.notesSearch). */
export const NOTES_SEARCH = { min: 2, max: 100 };

/** Every outcome a call can have, in the form's order, with the history's own last. */
export const ALL_OUTCOMES: Array<CallOutcome | 'UNKNOWN'> = [...OUTCOMES.map((o) => o.value), 'UNKNOWN'];

export const WHEN_CHOICES: Array<{ id: WhenId; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
  { id: '12m', label: 'Last 12 months' },
  { id: 'between', label: 'Between dates…' },
];

export const FOLLOW_UP_CHOICES: Array<{ id: FollowUpId; label: string; hint: string }> = [
  { id: 'any', label: 'Has a follow-up', hint: 'Open or done' },
  { id: 'open', label: 'Follow-up open', hint: 'Set and nobody has closed it' },
  { id: 'due', label: 'Follow-up due', hint: 'Open, and its time has come' },
  { id: 'done', label: 'Follow-up done', hint: 'Called back, or closed by hand' },
  { id: 'none', label: 'No follow-up', hint: 'None was set' },
];

export const SOURCE_CHOICES: Array<{ id: SourceId; label: string }> = [
  { id: 'panel', label: 'Logged in this panel' },
  { id: 'history', label: 'NationBuilder history' },
];

export const RECORDING_CHOICES: Array<{ id: RecordingId; label: string }> = [
  { id: 'with', label: 'With a recording' },
  { id: 'without', label: 'Without one' },
];

export const DIRECTION_CHOICES: Array<{ value: CallDirection; label: string }> = [
  { value: 'OUTBOUND', label: 'Outbound' },
  { value: 'INBOUND', label: 'Inbound' },
];

/** A real day: dayjs alone rolls 2026-13-45 over into 2027 rather than refusing it. */
const isDay = (value: string | null) => Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value).format('YYYY-MM-DD') === value);

const oneOf = <T extends string>(value: string | null, allowed: ReadonlyArray<T>): T | null =>
  value && (allowed as ReadonlyArray<string>).includes(value) ? (value as T) : null;

/** The state a URL holds. Anything unreadable is left out, never guessed. */
export const parseLogState = (params: URLSearchParams): CallLogState => {
  const person = Number(params.get('person'));
  const caller = params.get('caller') ?? '';
  const page = Number(params.get('page'));
  const size = Number(params.get('size'));
  return {
    notes: (params.get('q') ?? '').slice(0, NOTES_SEARCH.max),
    person: Number.isInteger(person) && person > 0 && person <= 2147483647 ? person : null,
    caller: /^(me|[ac][1-9]\d{0,9})$/.test(caller) ? caller : '',
    outcomes: (params.get('outcome') ?? '')
      .split(',')
      .filter((value, index, all): value is CallOutcome | 'UNKNOWN' => ALL_OUTCOMES.includes(value as CallOutcome) && all.indexOf(value) === index),
    method: oneOf(params.get('method'), METHODS.map((m) => m.value)),
    direction: oneOf(params.get('dir'), ['OUTBOUND', 'INBOUND'] as const),
    source: oneOf(params.get('source'), ['panel', 'history'] as const),
    when: oneOf(params.get('when'), WHEN_CHOICES.map((w) => w.id)),
    from: isDay(params.get('from')) ? (params.get('from') as string) : '',
    to: isDay(params.get('to')) ? (params.get('to') as string) : '',
    followUp: oneOf(params.get('followUp'), FOLLOW_UP_CHOICES.map((f) => f.id)),
    recording: oneOf(params.get('rec'), ['with', 'without'] as const),
    page: Number.isInteger(page) && page > 0 ? page : 0,
    pageSize: (PAGE_SIZES as ReadonlyArray<number>).includes(size) ? (size as PageSize) : DEFAULT_PAGE_SIZE,
  };
};

/** The URL for a state: only what differs from the empty one, so a plain log is a plain link. */
export const toLogParams = (state: CallLogState): URLSearchParams => {
  const params = new URLSearchParams();
  if (state.notes.trim()) params.set('q', state.notes.trim());
  if (state.person) params.set('person', String(state.person));
  if (state.caller) params.set('caller', state.caller);
  if (state.outcomes.length) params.set('outcome', state.outcomes.join(','));
  if (state.method) params.set('method', state.method);
  if (state.direction) params.set('dir', state.direction);
  if (state.source) params.set('source', state.source);
  if (state.when) params.set('when', state.when);
  if (state.when === 'between' && state.from) params.set('from', state.from);
  if (state.when === 'between' && state.to) params.set('to', state.to);
  if (state.followUp) params.set('followUp', state.followUp);
  if (state.recording) params.set('rec', state.recording);
  if (state.page) params.set('page', String(state.page));
  if (state.pageSize !== DEFAULT_PAGE_SIZE) params.set('size', String(state.pageSize));
  return params;
};

/** A change to what is shown starts again at the first page. */
export const withLogChange = (state: CallLogState, change: Partial<CallLogState>): CallLogState => ({
  ...state,
  ...change,
  page: 'page' in change ? (change.page as number) : 0,
});

export const hasLogFilters = (state: CallLogState) =>
  Boolean(
    state.notes.trim() ||
      state.person ||
      state.caller ||
      state.outcomes.length ||
      state.method ||
      state.direction ||
      state.source ||
      state.when ||
      state.followUp ||
      state.recording,
  );

/* ------------------------------------------------------------------ *
 * The crmCalls filter
 * ------------------------------------------------------------------ */

export interface CrmCallFilter {
  contactIds?: Array<number>;
  adminIds?: Array<number>;
  authorContactIds?: Array<number>;
  mine?: boolean;
  notesContain?: string;
  hasRecording?: boolean;
  outcomes?: Array<string>;
  methods?: Array<string>;
  directions?: Array<string>;
  sources?: Array<string>;
  occurredAt?: { from?: string; to?: string };
  followUp?: 'ANY' | 'OPEN' | 'DONE' | 'NONE';
  followUpAt?: { from?: string; to?: string };
}

/** Where a "last N days" range starts: that many whole days back, today included. */
const daysBack = (now: Dayjs, days: number) => now.startOf('day').subtract(days - 1, 'day');

/** The calls' time range, local days, `to` exclusive as the API takes it. */
export const occurredRange = (state: CallLogState, now: Dayjs): { from?: string; to?: string } | undefined => {
  const tomorrow = now.startOf('day').add(1, 'day');
  switch (state.when) {
    case 'today':
      return { from: now.startOf('day').toISOString(), to: tomorrow.toISOString() };
    case '7d':
      return { from: daysBack(now, 7).toISOString(), to: tomorrow.toISOString() };
    case '30d':
      return { from: daysBack(now, 30).toISOString(), to: tomorrow.toISOString() };
    case '90d':
      return { from: daysBack(now, 90).toISOString(), to: tomorrow.toISOString() };
    case '12m':
      return { from: now.startOf('day').subtract(12, 'month').add(1, 'day').toISOString(), to: tomorrow.toISOString() };
    case 'between': {
      // Either way round: the earlier day starts the range.
      const [first, last] = [state.from, state.to].filter(Boolean).length === 2 && state.from > state.to ? [state.to, state.from] : [state.from, state.to];
      const range = {
        ...(first ? { from: dayjs(first).startOf('day').toISOString() } : {}),
        ...(last ? { to: dayjs(last).startOf('day').add(1, 'day').toISOString() } : {}),
      };
      return Object.keys(range).length ? range : undefined;
    }
    default:
      return undefined;
  }
};

/**
 * The state as `crmCalls`' filter, or undefined for every call. `now` is
 * fixed by the screen while the URL stays the same, so the variables (and
 * Apollo's cache) stay put between renders.
 */
export const buildLogFilter = (state: CallLogState, now: Dayjs): CrmCallFilter | undefined => {
  const filter: CrmCallFilter = {};
  const notes = state.notes.trim();
  if (notes.length >= NOTES_SEARCH.min) filter.notesContain = notes;
  if (state.person) filter.contactIds = [state.person];
  if (state.caller === 'me') filter.mine = true;
  else if (state.caller.startsWith('a')) filter.adminIds = [Number(state.caller.slice(1))];
  else if (state.caller.startsWith('c')) filter.authorContactIds = [Number(state.caller.slice(1))];
  if (state.outcomes.length) filter.outcomes = [...state.outcomes];
  if (state.method) filter.methods = [state.method];
  if (state.direction) filter.directions = [state.direction];
  if (state.source === 'panel') filter.sources = ['MANUAL'];
  if (state.source === 'history') filter.sources = ['NATIONBUILDER', 'IMPORT'];
  const occurred = occurredRange(state, now);
  if (occurred) filter.occurredAt = occurred;
  if (state.recording) filter.hasRecording = state.recording === 'with';
  switch (state.followUp) {
    case 'any':
      filter.followUp = 'ANY';
      break;
    case 'open':
      filter.followUp = 'OPEN';
      break;
    case 'due':
      filter.followUp = 'OPEN';
      filter.followUpAt = { to: now.toISOString() };
      break;
    case 'done':
      filter.followUp = 'DONE';
      break;
    case 'none':
      filter.followUp = 'NONE';
      break;
  }
  return Object.keys(filter).length ? filter : undefined;
};

/* ------------------------------------------------------------------ *
 * Words
 * ------------------------------------------------------------------ */

const OUTCOME_LABELS: Record<string, string> = {
  ...Object.fromEntries(OUTCOMES.map((o) => [o.value, o.label])),
  UNKNOWN: 'Not recorded',
};

/** "Answered"; the history's UNKNOWN is "Not recorded". */
export const callOutcomeLabel = (outcome: string) => OUTCOME_LABELS[outcome] ?? outcome;

export type OutcomeTone = 'good' | 'retry' | 'bad' | 'other' | 'none';

/** How a chip colours each outcome: reached them, try again, stop, something else, not recorded. */
export const outcomeTone = (outcome: string): OutcomeTone => {
  switch (outcome) {
    case 'ANSWERED':
    case 'SPOKE':
      return 'good';
    case 'NOT_HOME':
    case 'LEFT_MESSAGE':
      return 'retry';
    case 'REFUSED':
    case 'BAD_INFO':
      return 'bad';
    case 'UNKNOWN':
      return 'none';
    default:
      return 'other';
  }
};

export const methodLabel = (method: string | null) => METHODS.find((m) => m.value === method)?.label ?? '';

/** "Phone, outbound"; the history records no direction. */
export const describeHow = (call: Pick<CallLogRow, 'method' | 'direction'>) =>
  [methodLabel(call.method), call.direction ? DIRECTION_CHOICES.find((d) => d.value === call.direction)?.label.toLowerCase() : '']
    .filter(Boolean)
    .join(', ');

const fullName = (person: { firstName: string | null; lastName: string | null } | null | undefined) =>
  person ? [person.firstName, person.lastName].filter(Boolean).join(' ') : '';

/** Who made the call: the admin, or the history's caller by name, or nobody known. */
export const callerName = (call: Pick<CallLogRow, 'admin' | 'author' | 'adminId' | 'authorContactId'>, myAdminId?: number | null) => {
  if (call.adminId != null && call.adminId === myAdminId) return 'You';
  return fullName(call.admin) || call.author?.fullName || (call.adminId != null ? `Admin #${call.adminId}` : call.authorContactId != null ? `Contact #${call.authorContactId}` : '');
};

/** A caller's choice in the caller menu: 'a<id>' or 'c<id>'. */
export const callerKey = (caller: Pick<CallerCount, 'adminId' | 'authorContactId'>) =>
  caller.adminId != null ? `a${caller.adminId}` : `c${caller.authorContactId}`;

export const callerCountName = (caller: CallerCount) =>
  caller.isYou ? 'You' : fullName(caller.admin) || caller.author?.fullName || (caller.adminId != null ? `Admin #${caller.adminId}` : `Contact #${caller.authorContactId}`);

/** "Tue 13 Oct, 21:19", the year too when it is not this one. `short`, for the table: "13 Oct, 21:19". */
export const formatWhen = (value: string, now: Dayjs, short = false) => {
  const at = dayjs(value);
  if (!at.isSame(now, 'year')) return at.format('D MMM YYYY, HH:mm');
  return at.format(short ? 'D MMM, HH:mm' : 'ddd D MMM, HH:mm');
};

export const personName = (contact: CallLogRow['contact'], contactId: number) =>
  contact?.fullName || contact?.primaryEmail || `Contact #${contactId}`;

/** The follow-up as the table says it, or '' for none. `short`: the day alone, "Due 15 Oct". */
export const describeFollowUp = (
  call: Pick<CallLogRow, 'followUpAt' | 'followUpDoneAt' | 'followUpOpen' | 'followUpDue'>,
  now: Dayjs,
  short = false,
) => {
  if (!call.followUpAt) return '';
  const day = (value: string) => dayjs(value).format(dayjs(value).isSame(now, 'year') ? 'D MMM' : 'D MMM YYYY');
  if (call.followUpDoneAt) return `Done ${day(call.followUpDoneAt)}`;
  if (short) return `${call.followUpDue ? 'Due' : 'Call'} ${day(call.followUpAt)}`;
  return `${call.followUpDue ? 'Due ' : ''}${formatWhen(call.followUpAt, now)}`;
};

export const describeCallCount = (count: number) => `${count.toLocaleString('en-US')} ${count === 1 ? 'call' : 'calls'}`;

/* ------------------------------------------------------------------ *
 * A correction (crmUpdateCall)
 * ------------------------------------------------------------------ */

export interface CorrectionForm {
  outcome: CallOutcome;
  method: CallMethod;
  direction: CallDirection;
  notes: string;
  /** As typed, like the log-a-call form's: "4:30", "5". */
  duration: string;
  supportLevel: number | null;
  /** A `datetime-local` value, or '' for none. */
  followUpAt: string;
  /** As typed; '' for none (card 21.7). */
  recordingUrl: string;
}

const LOCAL = 'YYYY-MM-DDTHH:mm';

/** The form for a call, as it is now. Only a call logged in this panel is corrected, so it has an outcome, method and direction. */
export const correctionFormOf = (call: CallLogRow): CorrectionForm => ({
  outcome: (call.outcome === 'UNKNOWN' ? 'ANSWERED' : call.outcome) as CallOutcome,
  method: call.method ?? 'PHONE',
  direction: call.direction ?? 'OUTBOUND',
  notes: call.notes ?? '',
  duration: call.durationSec ? formatDuration(call.durationSec) : '',
  supportLevel: call.supportLevel,
  followUpAt: call.followUpAt && !call.followUpDoneAt ? dayjs(call.followUpAt).format(LOCAL) : '',
  recordingUrl: call.recordingUrl ?? '',
});

export type CorrectionField = 'notes' | 'duration' | 'followUpAt' | 'recordingUrl';

/** What is wrong with the form, by field; empty when it can be sent. */
export const checkCorrection = (form: CorrectionForm, call: CallLogRow, now: Dayjs): Partial<Record<CorrectionField, string>> => {
  const problems: Partial<Record<CorrectionField, string>> = {};
  if (form.notes.trim().length > NOTES_MAX) problems.notes = `Notes are at most ${NOTES_MAX.toLocaleString('en-US')} characters.`;
  const seconds = parseDuration(form.duration);
  if (seconds !== null && Number.isNaN(seconds)) problems.duration = 'Write minutes, or minutes and seconds such as 4:30.';
  else if (seconds !== null && seconds > MAX_DURATION_SEC) problems.duration = 'At most 24 hours.';
  if (form.followUpAt) {
    const at = dayjs(form.followUpAt);
    if (!at.isValid()) problems.followUpAt = 'Not a valid date and time.';
    else if (!at.isAfter(dayjs(call.occurredAt))) problems.followUpAt = 'A follow-up comes after the call.';
    else if (at.isAfter(now.add(730, 'day'))) problems.followUpAt = 'At most two years ahead.';
  }
  const recording = checkRecordingUrl(form.recordingUrl);
  if (recording.problem) problems.recordingUrl = recording.problem;
  return problems;
};

export interface CallCorrection {
  outcome?: string;
  method?: string;
  direction?: string;
  notes?: string | null;
  durationSec?: number | null;
  supportLevel?: number | null;
  followUpAt?: string | null;
  recordingUrl?: string | null;
}

/** `crmUpdateCall`'s input: only what changed, null for what was cleared. Empty when nothing did. */
export const correctionInput = (call: CallLogRow, form: CorrectionForm): CallCorrection => {
  const was = correctionFormOf(call);
  const input: CallCorrection = {};
  if (form.outcome !== call.outcome) input.outcome = form.outcome;
  if (form.method !== call.method) input.method = form.method;
  if (form.direction !== call.direction) input.direction = form.direction;
  const notes = form.notes.trim();
  if (notes !== (call.notes ?? '')) input.notes = notes || null;
  const seconds = parseDuration(form.duration);
  if ((seconds ?? null) !== (call.durationSec ?? null)) input.durationSec = seconds;
  if ((form.supportLevel ?? null) !== (call.supportLevel ?? null)) input.supportLevel = form.supportLevel;
  if (form.followUpAt !== was.followUpAt) input.followUpAt = form.followUpAt ? dayjs(form.followUpAt).toISOString() : null;
  // Compared as the API stores it, so retyping the same link is no change. A refused link is
  // sent as typed, for checkCorrection to have stopped first.
  const recording = checkRecordingUrl(form.recordingUrl);
  const link = recording.problem ? form.recordingUrl.trim() : recording.url;
  if ((link ?? null) !== (call.recordingUrl ?? null)) input.recordingUrl = link || null;
  return input;
};

const CORRECTION_FIELDS: Record<string, CorrectionField> = {
  'input.notes': 'notes',
  'input.durationSec': 'duration',
  'input.followUpAt': 'followUpAt',
  'input.recordingUrl': 'recordingUrl',
};

/** What a refused correction means, in the dialog's words. */
export const describeCorrectionError = (error: ApolloError | Error): { message: string; field?: CorrectionField } => {
  const rejection = getRateLimitRejection(error);
  if (rejection) return { message: describeRateLimitRejection(rejection) };
  const graphQL = (error as ApolloError).graphQLErrors?.[0];
  const extensions = (graphQL?.extensions ?? {}) as Record<string, unknown>;
  switch (extensions.code) {
    case 'CRM_CALL_NOT_EDITABLE':
      return {
        message:
          extensions.reason === 'SOURCE'
            ? 'This call is part of the NationBuilder history and cannot be changed.'
            : 'Only the admin who logged a call can correct it.',
      };
    case 'CRM_CALL_NOT_FOUND':
      return { message: 'This call no longer exists.' };
    case 'CRM_INVALID_QUERY': {
      const field = CORRECTION_FIELDS[String(extensions.field)];
      return { message: graphQL?.message ?? 'The API refused the correction.', ...(field ? { field } : {}) };
    }
    default:
      return { message: 'The correction could not be saved. Try again.' };
  }
};

/** What a refused call log page means. */
export const describeLogError = (error: ApolloError): string => {
  const rejection = getRateLimitRejection(error);
  if (rejection) return describeRateLimitRejection(rejection);
  const graphQL = error.graphQLErrors?.[0];
  if (graphQL?.extensions?.code === 'CRM_INVALID_QUERY') return graphQL.message;
  return 'The call log could not be loaded. Try again in a moment.';
};
